import React, { useEffect, useMemo, useRef, useState } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import { readForecastFreezeRef } from '../../assets/forecastFreezeRef.js';
import DataTable from '../DataTable';
import { formatCurrency } from '../../utils/format';
import { IRR_NOMINAL_LABEL, effectiveAnnualFromNominal, evaluateDeal, evaluateAllScenarios } from '../../engine/dealEngine';
import { bidSliderMaxMM, defaultBidForDeal } from '../../engine/deskBid';
import {
  buildScenarioFreeze,
  compareFreezes,
  deskStateFromScenario,
  loadRegister,
  removeNamedScenario,
  upsertNamedScenario,
} from '../../engine/scenarioFreeze';
import {
  categoriesFromAssumptions,
  forceBuyerOverheadInCf,
  legacyOverheadFromCategories,
} from '../../engine/buyerOverhead';
import { summarizeCalibration } from '../../engine/calibration';
import { buildBoswellDcaChallenger } from '../../engine/dca';
import { createReleaseReadiness, evaluateReleaseReadiness } from '../../engine/releaseGate';
import { downloadBidLetterDocx, downloadIcMemoDocx, downloadOnePagerDocx } from '../../engine/capitalDocx';
import { downloadRegisterXlsx, downloadUnderwritingXlsx } from '../../engine/underwritingXlsx';
import {
  BID_PROCESS_DEFAULTS,
  buildBidSubmissionEmailDraft,
  createBidProposal,
  validateBidProposal,
} from '../../engine/bidProposal';
import { useAssistantPageExtras } from '../assistant/AssistantContext';
import { useAuth } from '../../auth/AuthContext';
import { withAroEstimate } from '../../aro/api';
import { fetchDeskJson } from '../../lib/deskDataFetch';
import { createDebouncedTracker, track } from '../../lib/track';
import {
  openDrillSelection,
  selectionFromScenarioMark,
  selectionFromWellMark,
} from '../../utils/drilldown';
import {
  coerceFinite,
  deskKnobsDiffer,
  loadDeskKnobs,
  parseNumericDraft,
  syncDeskToHash,
  rememberLiveDeskKnobs,
} from '../../engine/deskPersistence';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';
import { isMultiProductPackage } from '../../engine/productStreams';

function fmtIrr(v, undiscCfMM, bidPriceMM) {
  if (v == null || Number.isNaN(v)) {
    if (undiscCfMM != null && bidPriceMM != null && undiscCfMM < bidPriceMM) {
      return 'n/m (< bid undisc)';
    }
    return '—';
  }
  return `${(v * 100).toFixed(1)}%`;
}

function fmtMoic(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v.toFixed(2)}×`;
}

export default function Decision({ data, selectionContext }) {
  const { quote, quoteLabel, sellerAskMM, engineNote, midstreamAssignability, bidProcess } = data;
  const process = bidProcess || BID_PROCESS_DEFAULTS;
  const auth = useAuth();
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const { deal } = useDeal();
  const drillWell = (d) => openDrillSelection(selectionFromWellMark(d), { path: 'decision' });
  const drillScenario = (d) =>
    openDrillSelection(selectionFromScenarioMark(d), { path: 'decision' });
  const [pkg, setPkg] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [bidPriceMM, setBidPriceMM] = useState(
    () => defaultBidForDeal(deal?.dealId, null, { sellerAskMM }).bidPriceMM,
  );
  const [hh, setHh] = useState(3.42);
  const [boswellVolAdj, setBoswellVolAdj] = useState(0);
  const [netbackAdj, setNetbackAdj] = useState(0);
  const [discountRate, setDiscountRate] = useState(0.1);
  const [aroMM, setAroMM] = useState(0);
  const [buyerOhCategories, setBuyerOhCategories] = useState(() => categoriesFromAssumptions({}));
  const [freezeLabel, setFreezeLabel] = useState('Working base');
  const [freezeNote, setFreezeNote] = useState('');
  const [freezeApprover, setFreezeApprover] = useState('');
  const [register, setRegister] = useState(() => loadRegister());
  const [activeScenario, setActiveScenario] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [releaseReadiness, setReleaseReadiness] = useState(() => createReleaseReadiness());
  const [compareLeftId, setCompareLeftId] = useState('');
  const [compareRightId, setCompareRightId] = useState('');
  const [proposal, setProposal] = useState(() =>
    createBidProposal({
      meta: { caParty: 'Firebrand Energy, LLC', effectiveDate: process.effectiveDate, bidProcess: process },
      bidPriceMM: defaultBidForDeal(deal?.dealId, null, { sellerAskMM }).bidPriceMM,
    }),
  );

  const trackAssumption = useRef(createDebouncedTracker('assumption_changed', 450)).current;
  const trackOh = useRef(createDebouncedTracker('buyer_oh_category_edited', 450)).current;
  const compareTrackedRef = useRef('');
  const [restoreNote, setRestoreNote] = useState(null);
  const [persistReady, setPersistReady] = useState(false);
  const [hhDraft, setHhDraft] = useState(null);
  const [bidDraft, setBidDraft] = useState(null);
  const dealId = deal?.dealId;

  // Remount via App key={dealId} is primary; also gate persist before package reload.
  useEffect(() => {
    setPersistReady(false);
    setRestoreNote(null);
    setHhDraft(null);
    setBidDraft(null);
  }, [dealId]);

  useEffect(() => {
    let cancelled = false;
    fetchDeskJson(dealDataUrl(deal, 'deal-package.json', import.meta.env.BASE_URL), {
      refreshSession: () => auth.refresh(),
      errorMessage: (status) => `deal-package.json ${status}`,
    })
      .then((p) => (p && auth.isInternal ? withAroEstimate(p, dealId) : p))
      .then((p) => {
        if (cancelled || !p) return;
        setPkg(p);
        const isOilPkg = (p.meta?.primaryProduct || p.meta?.product) === 'oil';
        const multiPkg =
          isOilPkg &&
          (String(p.meta?.commodity || '').includes('gas') ||
            (Array.isArray(p.meta?.products) &&
              p.meta.products.some((x) => /gas|casinghead|residue/i.test(String(x)))));
        const pkgDefaults = {
          hh: isOilPkg
            ? (p.assumptions?.wti ??
              p.meta?.wtiBase ??
              (multiPkg ? 75 : p.assumptions?.hh) ??
              p.meta?.hhBase ??
              75)
            : (p.assumptions?.hh ?? p.meta?.hhBase ?? 3.42),
          bidPriceMM: defaultBidForDeal(dealId, p, { sellerAskMM }).bidPriceMM,
          boswellVolAdj: p.assumptions?.boswellVolAdj ?? 0,
          netbackAdj: p.assumptions?.netbackAdj ?? 0,
          discountRate: p.assumptions?.discountRate ?? 0.1,
          aroMM: p.assumptions?.aroMM ?? 0,
          buyerOverheadCategories: categoriesFromAssumptions(p.assumptions || {}),
        };
        const restored = loadDeskKnobs(pkgDefaults, { dealId });
        setHh(coerceFinite(restored.hh, pkgDefaults.hh));
        const resolvedBid = defaultBidForDeal(dealId, p, {
          sellerAskMM,
          storedBid: restored.bidPriceMM,
          bidEdited: false,
        });
        setBidPriceMM(coerceFinite(resolvedBid.bidPriceMM, pkgDefaults.bidPriceMM));
        setBoswellVolAdj(coerceFinite(restored.boswellVolAdj, 0));
        setNetbackAdj(coerceFinite(restored.netbackAdj, 0));
        setDiscountRate(coerceFinite(restored.discountRate, pkgDefaults.discountRate));
        // Configured ARO (D2D estimate / override) is authoritative over a stale stored knob.
        setAroMM(
          p.assumptions?.aroSource
            ? pkgDefaults.aroMM
            : Math.max(0, coerceFinite(restored.aroMM, 0)),
        );
        setBuyerOhCategories(
          restored.buyerOverheadCategories?.length
            ? categoriesFromAssumptions({
                buyerOverheadCategories: restored.buyerOverheadCategories,
              })
            : pkgDefaults.buyerOverheadCategories,
        );
        if (deskKnobsDiffer(restored, pkgDefaults)) {
          setRestoreNote('Restored desk knobs from URL / prior session');
        }
        setProposal((prev) =>
          createBidProposal({
            meta: p.meta || {},
            prior: prev,
            bidPriceMM: coerceFinite(
              defaultBidForDeal(dealId, p, { sellerAskMM, storedBid: restored.bidPriceMM }).bidPriceMM,
              pkgDefaults.bidPriceMM,
            ),
          }),
        );
        setReleaseReadiness((current) => {
          const empty = current.items?.every(
            (item) => !item.sourceRef && !item.owner && !item.summary,
          );
          return empty
            ? createReleaseReadiness({ asOf: p.meta?.generatedAt || p.meta?.priceDeck || '' })
            : current;
        });
        setPersistReady(true);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [sellerAskMM, deal, dealId, auth.refresh]);

  // Persist knobs: live memory + storage immediately; debounce hash only while on this tab.
  useEffect(() => {
    if (!persistReady) return undefined;
    const knobs = {
      hh,
      bidPriceMM,
      boswellVolAdj,
      netbackAdj,
      discountRate,
      aroMM,
      buyerOverheadCategories: buyerOhCategories,
    };
    rememberLiveDeskKnobs(knobs, { dealId });
    const t = window.setTimeout(() => {
      syncDeskToHash(knobs, { path: 'decision' });
    }, 200);
    return () => {
      window.clearTimeout(t);
      // Storage/live only on cleanup — never rewrite hash path after the user navigated away.
      rememberLiveDeskKnobs(knobs, { dealId });
    };
  }, [persistReady, hh, bidPriceMM, boswellVolAdj, netbackAdj, discountRate, aroMM, buyerOhCategories, dealId]);

  useEffect(() => {
    setProposal((prev) =>
      prev.purchasePriceMM === bidPriceMM ? prev : { ...prev, purchasePriceMM: bidPriceMM },
    );
  }, [bidPriceMM]);

  const legacyOh = useMemo(() => legacyOverheadFromCategories(buyerOhCategories), [buyerOhCategories]);

  function handleBuyerOhChange(id, patch) {
    setBuyerOhCategories((cats) => {
      const prev = cats.find((c) => c.id === id);
      const next = cats.map((c) => {
        if (c.id !== id) return c;
        const merged = { ...c, ...patch };
        if ('monthly' in patch) {
          const n = Number(patch.monthly);
          merged.monthly = Number.isFinite(n) ? Math.max(0, n) : c.monthly;
        }
        return merged;
      });
      const updated = next.find((c) => c.id === id);
      trackOh({
        category_id: id,
        category_label: updated?.label || id,
        monthly: updated?.monthly,
        include_in_cf: !!updated?.includeInCf,
        changed_field: Object.keys(patch)[0] || 'unknown',
        previous_monthly: prev?.monthly,
      });
      return next;
    });
  }

  function setAssumption(field, value, setter) {
    setter(value);
    trackAssumption({ field, value, source: 'slider' });
  }

  /** Commit finite text drafts only — empty / intermediate edits stay local until blur. */
  function onPriceDraftChange(raw, setDraft, field, setter, { min = 0 } = {}) {
    setDraft(raw);
    if (raw === '' || raw === '.' || raw === '-' || raw === '-.') return;
    const parsed = parseNumericDraft(raw, { empty: null, fallback: null, min });
    if (parsed == null) return;
    setter(parsed);
    trackAssumption({ field, value: parsed, source: 'text' });
  }

  function onPriceDraftBlur(draft, setDraft, field, setter, current, { min = 0 } = {}) {
    if (draft == null) return;
    if (draft === '' || draft === '.' || draft === '-' || draft === '-.') {
      setDraft(null);
      return;
    }
    const parsed = parseNumericDraft(draft, { empty: null, fallback: current, min });
    const value = coerceFinite(parsed, current);
    setter(value);
    trackAssumption({ field, value, source: 'text_blur' });
    setDraft(null);
  }

  const isOilDeal = (pkg?.meta?.primaryProduct || pkg?.meta?.product) === 'oil';
  const multiProduct = Boolean(pkg && isMultiProductPackage(pkg));
  // Oil-primary desk keeps a single price slider (WTI). Multi-product packages
  // still use that slider for oil; gas HH stays on package assumptions (independent stress via scenarios).
  const packageGasHh = multiProduct
    ? (pkg?.assumptions?.hh ?? pkg?.meta?.hhBase ?? 3.42)
    : null;
  const priceBase = isOilDeal
    ? (pkg?.meta?.wtiBase ?? (multiProduct ? 75 : pkg?.meta?.hhBase) ?? 75)
    : (pkg?.meta?.hhBase ?? 3.42);
  const priceMax = isOilDeal ? 120 : 4.5;
  const priceStep = isOilDeal ? 0.5 : 0.05;
  const priceLabel = isOilDeal ? 'WTI ($/bbl)' : 'HH ($/MMBtu)';
  const priceAria = isOilDeal ? 'WTI strip price' : 'HH strip price';
  const volLabel = isOilDeal ? 'Concentration vol' : 'Boswell vol';
  const bearPriceStress = isOilDeal ? 5 : 0.5;
  const bearNetbackStress = isOilDeal ? 2.5 : 0.15;

  const overrides = useMemo(() => {
    const o = {
      boswellVolAdj,
      netbackAdj,
      discountRate,
      aroMM,
      buyerOverheadCategories: buyerOhCategories,
      overheadInCf: legacyOh.overheadInCf,
      overheadMonthly: legacyOh.overheadMonthly,
      bidPriceMM,
    };
    if (isOilDeal && multiProduct) {
      o.wti = hh;
      o.hh = packageGasHh;
    } else if (isOilDeal) {
      o.hh = hh;
      o.wti = hh;
    } else {
      o.hh = hh;
    }
    return o;
  }, [
    hh,
    boswellVolAdj,
    netbackAdj,
    discountRate,
    aroMM,
    buyerOhCategories,
    legacyOh,
    bidPriceMM,
    isOilDeal,
    multiProduct,
    packageGasHh,
  ]);

  const base = useMemo(() => (pkg ? evaluateDeal(pkg, overrides) : null), [pkg, overrides]);

  const taxRateDefaulted = Boolean(pkg && (pkg.meta?.taxRate == null || pkg.meta?.taxRate === ''));

  const bear = useMemo(() => {
    if (!pkg) return null;
    const stressed = Math.min(hh, priceBase - bearPriceStress);
    const stressOverrides = {
      ...overrides,
      boswellVolAdj: Math.min(boswellVolAdj, -0.25),
      netbackAdj: Math.min(netbackAdj, -bearNetbackStress),
    };
    if (isOilDeal && multiProduct) {
      stressOverrides.wti = stressed;
      stressOverrides.hh = packageGasHh;
    } else if (isOilDeal) {
      stressOverrides.hh = stressed;
      stressOverrides.wti = stressed;
    } else {
      stressOverrides.hh = stressed;
    }
    return evaluateDeal(pkg, forceBuyerOverheadInCf(stressOverrides));
  }, [
    pkg,
    overrides,
    hh,
    boswellVolAdj,
    netbackAdj,
    isOilDeal,
    multiProduct,
    packageGasHh,
    priceBase,
    bearPriceStress,
    bearNetbackStress,
  ]);

  const scenarioRows = useMemo(() => {
    if (!pkg) return [];
    // Same engine path as Sensitivity: desk knobs as base, scenario deltas win.
    return evaluateAllScenarios(pkg, overrides).map((s) => ({
      id: s.id,
      scenario: s.label,
      pv: +s.result.pvMM.toFixed(2),
      ntm: +s.result.ntmCfMM.toFixed(2),
      irr: s.result.irrAnnual,
      bridge: +s.result.bidBridgePvMM.toFixed(2),
      x5: +s.result.multiples.x5.toFixed(2),
    }));
  }, [pkg, overrides]);

  const calibration = useMemo(() => (pkg ? summarizeCalibration(pkg) : null), [pkg]);

  const dca = useMemo(() => {
    if (!pkg?.actuals?.boswell) return null;
    const ariesAsset = pkg.assets?.find((a) => a.isBoswell);
    return buildBoswellDcaChallenger({
      actuals: pkg.actuals.boswell,
      ariesQi: ariesAsset?.qiMcfd || ariesAsset?.qiBopd || null,
      ariesDeAnnual: ariesAsset?.deAnnual || 0.06,
      startDate: (ariesAsset?.start || '2026-06-01').slice(0, 7),
    });
  }, [pkg]);

  const releaseGate = useMemo(
    () =>
      evaluateReleaseReadiness(releaseReadiness, {
        asOf: pkg?.meta?.generatedAt || pkg?.meta?.priceDeck || '',
      }),
    [pkg, releaseReadiness],
  );

  const lean =
    base && bear
      ? base.irrAnnual == null
        ? 'Bid above undisc CF — reprice'
        : bear.irrAnnual == null
          ? 'Stress undisc < bid — high risk'
          : base.irrAnnual >= 0.12 && bear.irrAnnual >= 0.0 && base.boswellShare < 0.65
            ? 'Lean bid'
            : base.irrAnnual >= 0.08
              ? 'Borderline — stress Boswell / netback'
              : 'Lean pass / reprice'
      : 'Set bid to compute returns';

  useEffect(() => {
    if (!base) {
      clearPageExtras();
      return undefined;
    }
    setPageExtras({
      tab: 'decision',
      bidPriceMM,
      hh,
      boswellVolAdj,
      netbackAdj,
      discountRate,
      aroMM,
      buyerOhMonthlyTotal: base.buyerOverhead?.monthlyTotal ?? null,
      buyerOhMonthlyIncluded: base.buyerOverhead?.monthlyIncluded ?? null,
      pvLabel: base.pvLabel,
      pvMM: +base.pvMM.toFixed(3),
      bidBridgePvMM: +base.bidBridgePvMM.toFixed(3),
      ntmCfMM: +base.ntmCfMM.toFixed(3),
      boswellShare: +base.boswellShare.toFixed(4),
      baseIrr: base.irrAnnual,
      irrWithoutBuyerOh: base.irrWithoutBuyerOh ?? null,
      stressIrr: bear?.irrAnnual ?? null,
      moic: base.moic,
      liabilityRowCount: base.liabilitySchedule?.rowCount ?? 0,
      recommendation: lean,
      midstreamStatus: midstreamAssignability?.overallStatus ?? null,
    });
    return () => clearPageExtras();
  }, [
    base,
    bear,
    bidPriceMM,
    hh,
    boswellVolAdj,
    netbackAdj,
    discountRate,
    aroMM,
    lean,
    midstreamAssignability,
    setPageExtras,
    clearPageExtras,
  ]);

  const freezePreview = useMemo(() => {
    if (!pkg || !base || !bear) return null;
    return buildScenarioFreeze({
      pkg,
      assumptions: overrides,
      label: freezeLabel,
      note: freezeNote,
      approver: freezeApprover,
      recommendation: lean,
      midstream: midstreamAssignability,
      releaseReadiness,
      postCloseActuals: pkg?.postCloseActuals,
      base,
      stress: bear,
      scenarioRows,
      dca,
      proposal: { ...proposal, purchasePriceMM: bidPriceMM },
      forecastFreezeRef: readForecastFreezeRef(pkg?.meta?.dealId || 'eagle-etx-2026'),
    });
  }, [
    pkg,
    base,
    bear,
    overrides,
    freezeLabel,
    freezeNote,
    freezeApprover,
    lean,
    midstreamAssignability,
    releaseReadiness,
    scenarioRows,
    dca,
    proposal,
    bidPriceMM,
  ]);

  const exportSource = activeScenario || freezePreview;
  const proposalGate = useMemo(
    () => validateBidProposal({ ...proposal, purchasePriceMM: bidPriceMM }),
    [proposal, bidPriceMM],
  );

  function patchProposal(patch) {
    setProposal((prev) => ({ ...prev, ...patch }));
    setSaveMsg(null);
  }

  function patchContact(idx, patch) {
    setProposal((prev) => {
      const contacts = [...(prev.contacts || [])];
      contacts[idx] = { ...contacts[idx], ...patch };
      return { ...prev, contacts };
    });
    setSaveMsg(null);
  }

  function patchContingency(idx, value) {
    setProposal((prev) => {
      const contingencies = [...(prev.contingencies || [])];
      contingencies[idx] = value;
      return { ...prev, contingencies };
    });
  }

  function patchSubmission(patch) {
    setProposal((prev) => ({
      ...prev,
      submission: { ...(prev.submission || {}), ...patch },
    }));
  }

  const compareResult = useMemo(() => {
    const left = register.find((f) => f.scenarioId === compareLeftId);
    const right = register.find((f) => f.scenarioId === compareRightId);
    return left && right ? compareFreezes(left, right) : null;
  }, [register, compareLeftId, compareRightId]);

  useEffect(() => {
    if (!compareResult || !compareLeftId || !compareRightId) return;
    const key = `${compareLeftId}|${compareRightId}`;
    if (compareTrackedRef.current === key) return;
    compareTrackedRef.current = key;
    track('scenario_compared', {
      left_id: compareLeftId,
      right_id: compareRightId,
    });
  }, [compareResult, compareLeftId, compareRightId]);

  function handleSaveScenario() {
    if (!freezePreview) return;
    const name = freezeLabel.trim();
    if (!name) {
      setSaveMsg('Enter a scenario name before saving.');
      return;
    }
    const frozen = { ...freezePreview, label: name, frozenAt: new Date().toISOString() };
    try {
      const next = upsertNamedScenario(frozen);
      setRegister(next);
      setActiveScenario(frozen);
      setSaveMsg(`Saved "${name}" — load it anytime from the list below.`);
      track('scenario_saved', {
        content_hash: frozen.contentHash,
        label: frozen.label,
        bid_price_mm: frozen.assumptions.bidPriceMM,
        hh: frozen.assumptions.hh,
        boswell_vol_adj: frozen.assumptions.boswellVolAdj,
      });
    } catch (err) {
      setSaveMsg(err.message || 'Save failed');
    }
  }

  function handleLoadScenario(scenarioId) {
    const frozen = register.find((f) => f.scenarioId === scenarioId);
    if (!frozen) return;
    const desk = deskStateFromScenario(frozen);
    setBidPriceMM(desk.bidPriceMM);
    setHh(desk.hh);
    setBoswellVolAdj(desk.boswellVolAdj);
    setNetbackAdj(desk.netbackAdj);
    setDiscountRate(desk.discountRate);
    setAroMM(desk.aroMM);
    setBuyerOhCategories(
      desk.buyerOverheadCategories?.length
        ? desk.buyerOverheadCategories
        : categoriesFromAssumptions({
            overheadInCf: desk.overheadInCf,
            overheadMonthly: desk.overheadMonthly,
          }),
    );
    setFreezeLabel(desk.freezeLabel);
    setFreezeNote(desk.freezeNote);
    setFreezeApprover(desk.freezeApprover);
    setProposal(
      createBidProposal({
        meta: pkg?.meta || { bidProcess: process },
        prior: desk.proposal || null,
        bidPriceMM: desk.bidPriceMM,
      }),
    );
    setActiveScenario(frozen);
    setReleaseReadiness(
      frozen.releaseReadiness ||
        createReleaseReadiness({ asOf: pkg?.meta?.generatedAt || pkg?.meta?.priceDeck || '' }),
    );
    setSaveMsg(`Loaded "${frozen.label}" onto the desk.`);
    track('scenario_loaded', {
      content_hash: frozen.contentHash,
      label: frozen.label,
    });
  }

  function handleDeleteScenario(scenarioId) {
    const target = register.find((f) => f.scenarioId === scenarioId);
    if (!target) return;
    if (!window.confirm(`Delete saved scenario "${target.label}"?`)) return;
    const next = removeNamedScenario(scenarioId);
    setRegister(next);
    if (activeScenario?.scenarioId === scenarioId) setActiveScenario(null);
    setSaveMsg(`Deleted "${target.label}".`);
    track('scenario_deleted', { content_hash: target.contentHash, label: target.label });
  }

  function handleExportOnePager() {
    const f = exportSource;
    if (!f) return;
    const gate = evaluateReleaseReadiness(f.releaseReadiness, { asOf: f.asOf || '' });
    if (!gate.ready) {
      track('export_failed', { export_type: 'capital_one_pager', reason: 'release_gate' });
      setSaveMsg(
        'External one-pager is blocked until all release-readiness items are cleared or disclosed.',
      );
      return;
    }
    try {
      downloadOnePagerDocx(f);
    } catch (err) {
      track('export_failed', {
        export_type: 'capital_one_pager',
        reason: String(err.message || err).slice(0, 120),
      });
      setSaveMsg(err.message || 'External one-pager export blocked.');
      return;
    }
    track('capital_one_pager_exported', {
      content_hash: f.contentHash,
      label: f.label,
      format: 'docx',
    });
  }

  function handleExportRegister() {
    const rows = register.length ? register : freezePreview ? [freezePreview] : [];
    if (!rows.length) return;
    downloadRegisterXlsx(rows);
    track('scenario_register_exported', { count: rows.length, format: 'xlsx' });
  }

  function handleExportUnderwriting() {
    const f = exportSource;
    if (!f) return;
    downloadUnderwritingXlsx(f);
    track('underwriting_xlsx_exported', {
      content_hash: f.contentHash,
      label: f.label,
    });
  }

  function updateReleaseItem(category, patch) {
    setReleaseReadiness((current) => ({
      ...current,
      items: (current.items || []).map((item) =>
        item.category === category ? { ...item, ...patch } : item,
      ),
    }));
    setSaveMsg(null);
  }

  function handleExportIcMemo() {
    const f = exportSource;
    if (!f) return;
    downloadIcMemoDocx(f);
    track('ic_memo_exported', {
      content_hash: f.contentHash,
      label: f.label,
      recommendation: f.recommendation,
      format: 'docx',
    });
  }

  function handleExportBidLetter() {
    const f = freezePreview || exportSource;
    if (!f) return;
    const draft = { ...proposal, purchasePriceMM: bidPriceMM };
    const gate = validateBidProposal(draft);
    if (!gate.ok) {
      track('export_failed', { export_type: 'bid_letter', reason: 'proposal_incomplete' });
      setSaveMsg(`Bid letter blocked: ${gate.blockers.join('; ')}`);
      return;
    }
    try {
      downloadBidLetterDocx(f, draft);
    } catch (err) {
      track('export_failed', {
        export_type: 'bid_letter',
        reason: String(err.message || err).slice(0, 120),
      });
      setSaveMsg(err.message || 'Bid letter export failed.');
      return;
    }
    track('bid_letter_exported', {
      content_hash: f.contentHash,
      label: f.label,
      purchase_price_mm: draft.purchasePriceMM,
      format: 'docx',
    });
    setSaveMsg('Bid letter downloaded — convert to PDF on company letterhead before emailing RedOaks.');
  }

  async function handleCopyBidEmail() {
    const draft = buildBidSubmissionEmailDraft(
      { ...proposal, purchasePriceMM: bidPriceMM },
      { process },
    );
    const text = [
      `To: ${draft.to}`,
      `Cc: ${draft.cc}`,
      `Subject: ${draft.subject}`,
      '',
      draft.body,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      track('bid_submission_email_copied', { to: draft.to });
      setSaveMsg('Email draft copied — attach the letterhead PDF and send before noon CT Aug 5.');
    } catch {
      setSaveMsg('Could not copy email draft — select and copy manually from the console if needed.');
    }
  }

  const openingBid = defaultBidForDeal(dealId, pkg, { sellerAskMM });
  const bidSliderMax = bidSliderMaxMM(Math.max(
    Number(bidPriceMM) || 0,
    Number(openingBid.bidPriceMM) || 0,
  ));
  const go =
    base &&
    bear &&
    base.irrAnnual != null &&
    bear.irrAnnual != null &&
    base.irrAnnual >= 0.12 &&
    bear.irrAnnual >= 0.0 &&
    base.boswellShare < 0.65;

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Decision · Bid desk</h2>
          <p className="page-subtitle">
            Concentration first · risked PV &amp; IRR at your bid · seller-calibrated model
          </p>
          {quote && (
            <div className="page-quote">
              <span className="page-quote-label">{quoteLabel}</span>
              <span>&ldquo;{quote}&rdquo;</span>
            </div>
          )}
          {engineNote && <p className="engine-note">{engineNote}</p>}
          {loadError && <p className="engine-note error">Engine load failed: {loadError}</p>}
        </div>
        {base && (
          <div className={`decision-verdict ${go ? 'go' : 'hold'}`}>
            <div className="decision-verdict-label">Working signal</div>
            <div className="decision-verdict-value">{lean}</div>
            <div className="decision-verdict-sub">
              Heuristic only — not IC approval. Needs Boswell proof + midstream assignability.
            </div>
          </div>
        )}
      </div>

      {base && (
        <section className="dashboard-section">
          <div className="concentration-banner">
            <strong>Concentration:</strong>{' '}
            {isOilDeal ? 'Top well' : 'Boswell'} {(base.boswellShare * 100).toFixed(0)}% model-basis share of
            modeled PDP PV · top-well package — treat as single-well risked bid, not diversified PDP.
          </div>
          {midstreamAssignability && !isOilDeal && (
            <div className={`assignability-banner ${midstreamAssignability.overallStatus}`}>
              <strong>Midstream assignability: {midstreamAssignability.overallStatus.toUpperCase()}</strong>
              {' · '}
              {midstreamAssignability.openCount}/{midstreamAssignability.contractCount} contracts open
              {' · '}
              L12M realized ${midstreamAssignability.l12Realized?.toFixed(2)}/Mcf vs stack ~$
              {midstreamAssignability.stackAt6k?.toFixed(2)} @ 6k — see Sensitivity register.
            </div>
          )}
        </section>
      )}

      {pkg && (
        <section className="dashboard-section">
          <div className="chart-card assumption-panel">
            <div className="chart-title">Bid bridge inputs</div>
            <div className="chart-subtitle">
              Model base is decline-reconstructed + cfScale-calibrated to seller oneline — fact vs
              judgment labeled below. Knobs persist in the URL hash and local session so a stress
              run survives refresh / deep-link.
            </div>
            {restoreNote && (
              <p className="engine-note desk-restore-note" role="status">
                {restoreNote}{' '}
                <button
                  type="button"
                  className="linkish"
                  onClick={() => {
                    if (!pkg) return;
                    const defaults = {
                      hh: isOilDeal
                        ? (pkg.assumptions?.wti ??
                          pkg.meta?.wtiBase ??
                          pkg.assumptions?.hh ??
                          pkg.meta?.hhBase ??
                          75)
                        : (pkg.assumptions?.hh ?? pkg.meta?.hhBase ?? 3.42),
                      bidPriceMM: defaultBidForDeal(dealId, pkg, { sellerAskMM }).bidPriceMM,
                      boswellVolAdj: pkg.assumptions?.boswellVolAdj ?? 0,
                      netbackAdj: pkg.assumptions?.netbackAdj ?? 0,
                      discountRate: pkg.assumptions?.discountRate ?? 0.1,
                      aroMM: pkg.assumptions?.aroMM ?? 0,
                    };
                    setHh(defaults.hh);
                    setBidPriceMM(defaults.bidPriceMM);
                    setBoswellVolAdj(defaults.boswellVolAdj);
                    setNetbackAdj(defaults.netbackAdj);
                    setDiscountRate(defaults.discountRate);
                    setAroMM(defaults.aroMM);
                    setBuyerOhCategories(categoriesFromAssumptions(pkg.assumptions || {}));
                    setRestoreNote(null);
                  }}
                >
                  Reset to package defaults
                </button>
              </p>
            )}
            <div className="assumption-grid">
              <label className="assumption-control">
                <span>
                  Bid price ($MM)
                  {openingBid.label
                    && bidPriceMM != null
                    && Math.abs(Number(bidPriceMM) - Number(openingBid.bidPriceMM)) < 0.051
                    ? ` · ${openingBid.label}`
                    : ''}
                </span>
                <input
                  type="range"
                  min={0}
                  max={bidSliderMax}
                  step={0.25}
                  value={Math.min(bidSliderMax, Math.max(0, Number(bidPriceMM) || 0))}
                  onChange={(e) => {
                    setBidDraft(null);
                    setAssumption('bidPriceMM', Number(e.target.value), setBidPriceMM);
                  }}
                />
                <input
                  className="assumption-number"
                  type="number"
                  min={0}
                  step={0.1}
                  value={bidDraft != null ? bidDraft : bidPriceMM}
                  aria-label="Bid price $MM"
                  onChange={(e) =>
                    onPriceDraftChange(e.target.value, setBidDraft, 'bidPriceMM', setBidPriceMM)
                  }
                  onBlur={() =>
                    onPriceDraftBlur(bidDraft, setBidDraft, 'bidPriceMM', setBidPriceMM, bidPriceMM)
                  }
                />
              </label>
              <label className="assumption-control">
                <span>{priceLabel}</span>
                <input
                  type="range"
                  min={0}
                  max={priceMax}
                  step={priceStep}
                  value={Math.min(priceMax, Math.max(0, hh))}
                  onChange={(e) => {
                    setHhDraft(null);
                    setAssumption(isOilDeal ? 'wti' : 'hh', Number(e.target.value), setHh);
                  }}
                />
                <input
                  className="assumption-number"
                  type="number"
                  min={0}
                  step={priceStep}
                  value={hhDraft != null ? hhDraft : hh}
                  aria-label={priceAria}
                  onChange={(e) =>
                    onPriceDraftChange(e.target.value, setHhDraft, isOilDeal ? 'wti' : 'hh', setHh)
                  }
                  onBlur={() =>
                    onPriceDraftBlur(hhDraft, setHhDraft, isOilDeal ? 'wti' : 'hh', setHh, hh)
                  }
                />
              </label>
              <label className="assumption-control">
                <span>
                  {volLabel} ({(boswellVolAdj * 100).toFixed(0)}%)
                </span>
                <input
                  type="range"
                  min={-0.5}
                  max={0.2}
                  step={0.01}
                  value={boswellVolAdj}
                  onChange={(e) =>
                    setAssumption('boswellVolAdj', Number(e.target.value), setBoswellVolAdj)
                  }
                />
              </label>
              <label className="assumption-control">
                <span>Netback adj (${netbackAdj.toFixed(2)}/Mcf)</span>
                <input
                  type="range"
                  min={-0.5}
                  max={0.25}
                  step={0.01}
                  value={netbackAdj}
                  onChange={(e) => setAssumption('netbackAdj', Number(e.target.value), setNetbackAdj)}
                />
              </label>
              <label className="assumption-control">
                <span>
                  Discount ({(discountRate * 100).toFixed(0)}% a year, applied monthly) → {base?.pvLabel}
                </span>
                <input
                  type="range"
                  min={0.05}
                  max={0.3}
                  step={0.01}
                  value={discountRate}
                  onChange={(e) =>
                    setAssumption('discountRate', Number(e.target.value), setDiscountRate)
                  }
                />
              </label>
              <label className="assumption-control">
                <span>ARO / P&amp;A (${aroMM.toFixed(1)}MM)</span>
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={0.1}
                  value={aroMM}
                  onChange={(e) => setAssumption('aroMM', Number(e.target.value), setAroMM)}
                />
              </label>
            </div>
            <div className="buyer-oh-panel">
              <div className="chart-title" style={{ fontSize: 13 }}>
                Buyer overhead (categories · judgment)
              </div>
              <p className="engine-note" style={{ margin: '2px 0 8px' }}>
                Buyer-side G&amp;A overlays — desk judgment, not seller facts. Edit $/mo and toggle
                In CF; custom amounts flow into live CF, PV, and IRR. The ~$53k/mo total is an
                internal illustration until sourced/mapped. Buyer G&amp;A stops after the last month
                with positive operating cash flow (the package economic limit). Seller G&amp;A / LOE
                clarity lives on the Cash Flow tab (#94).
              </p>
              {buyerOhCategories.map((c) => (
                <div className="buyer-oh-row" key={c.id}>
                  <span className="buyer-oh-label">{c.label}</span>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    value={Number.isFinite(c.monthly) ? c.monthly : 0}
                    aria-label={`${c.label} monthly overhead`}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        handleBuyerOhChange(c.id, { monthly: 0 });
                        return;
                      }
                      const n = Number(raw);
                      if (Number.isFinite(n)) handleBuyerOhChange(c.id, { monthly: Math.max(0, n) });
                    }}
                  />
                  <label className="assumption-check">
                    <input
                      type="checkbox"
                      checked={!!c.includeInCf}
                      onChange={(e) => handleBuyerOhChange(c.id, { includeInCf: e.target.checked })}
                    />
                    <span>In CF</span>
                  </label>
                </div>
              ))}
              <p className="engine-note" style={{ margin: '8px 0 0' }}>
                Total {formatCurrency(legacyOh.overheadMonthly)}/mo · included in CF{' '}
                {formatCurrency(base?.buyerOverhead?.monthlyIncluded)}/mo · PV impact{' '}
                {base?.buyerOhPvDeltaMM != null ? `$${base.buyerOhPvDeltaMM.toFixed(2)}MM` : '—'} · IRR
                w/o buyer OH {fmtIrr(base?.irrWithoutBuyerOh)}
              </p>
            </div>
          </div>
        </section>
      )}

      {base && bear && (
        <>
          {taxRateDefaulted && (
            <p className="muted" data-testid="decision-tax-default-label" style={{ marginBottom: 8 }}>
              Tax rate defaulted to 7.5% — not from package
            </p>
          )}
          <KPIGrid>
            <KPICard
              label={`Gross ${base.pvLabel}`}
              value={formatCurrency(base.pvMM * 1e6)}
              sub={`Seller PDP ref $${pkg?.validation?.refPdpPv10MM ?? '—'}MM · Δ $${base.calibration?.modelVsRefPdpMM ?? '—'}MM`}
              color="var(--navy)"
            />
            <KPICard
              label="After ARO (bridge)"
              value={formatCurrency(base.bidBridgePvMM * 1e6)}
              sub={`ARO $${aroMM.toFixed(1)}MM deducted`}
              color="var(--amber)"
            />
            <KPICard
              label={`${IRR_NOMINAL_LABEL} at bid (base)`}
              value={fmtIrr(base.irrAnnual, base.undiscCfMM, bidPriceMM)}
              sub={`effective ${fmtIrr(base.irrEffective ?? effectiveAnnualFromNominal(base.irrAnnual))} · MOIC ${fmtMoic(base.moic)} · bid $${Number(bidPriceMM || 0).toFixed(1)}MM · IRR w/o buyer OH ${fmtIrr(base.irrWithoutBuyerOh)}`}
              color="var(--green)"
            />
            <KPICard
              label={`${IRR_NOMINAL_LABEL}, stress case`}
              value={fmtIrr(bear.irrAnnual, bear.undiscCfMM, bidPriceMM)}
              sub={
                isOilDeal
                  ? 'WTI−$5 · Conc−25% · netback−$2.50 · all buyer OH categories in CF'
                  : 'HH−0.50 · Bos−25% · netback−$0.15 · all buyer OH categories in CF'
              }
              color="var(--red)"
            />
          </KPIGrid>

          <section className="dashboard-section" id="liability-schedule">
            <div className="section-title">Liability schedule · negative PV / inactive (not floored)</div>
            <div className="chart-card">
              <p className="chart-subtitle" style={{ marginBottom: 12 }}>
                Wells carrying negative oneline PV or inactive/shut-in flags. These remain in package
                totals — never floored to zero — and are tracked separately for P&amp;A / plug
                liability review.
              </p>
              {base.liabilitySchedule?.rowCount ? (
                <DataTable
                  columns={[
                    { key: 'lease', label: 'Lease' },
                    { key: 'onelinePv', label: 'Oneline PV', format: (v) => formatCurrency(v) },
                    {
                      key: 'modelAssetPv',
                      label: 'Model asset PV',
                      format: (v) => (v == null ? '—' : formatCurrency(v)),
                    },
                    {
                      key: 'suggestedPlugAroShareMM',
                      label: 'Plug/ARO share',
                      format: (v) => (v == null ? '—' : `$${v.toFixed(3)}MM`),
                    },
                    { key: 'inactive', label: 'Inactive', format: (v) => (v ? 'Yes' : 'No') },
                    { key: 'regulatoryNote', label: 'Note' },
                  ]}
                  data={base.liabilitySchedule.rows}
                  defaultSort="onelinePv"
                  defaultDir="asc"
                  onRowClick={(row) => {
                    track('liability_row_opened', {
                      lease: row.lease,
                      api: row.api || undefined,
                      oneline_pv: row.onelinePv,
                      inactive: !!row.inactive,
                    });
                    drillWell(row);
                  }}
                />
              ) : (
                <p className="engine-note">No negative-PV / inactive wells flagged at current knobs.</p>
              )}
              <p className="engine-note" style={{ marginTop: 10 }}>
                Oneline negative total {formatCurrency(base.liabilitySchedule?.onelineNegativeTotal)} ·
                model negative total {formatCurrency(base.liabilitySchedule?.modelNegativeTotal)} ·
                package PV never floors negative assets to zero.
              </p>
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-title">Price → return bridge ($MM)</div>
            <div className="bid-bridge">
              <div className="bid-bridge-step">
                <span className="tag tag-model">Model</span>
                <div className="bid-bridge-label">Gross {base.pvLabel}</div>
                <div className="bid-bridge-value">${base.pvMM.toFixed(2)}</div>
              </div>
              <div className="bid-bridge-arrow">→</div>
              <div className="bid-bridge-step">
                <span className="tag tag-judgment">Judgment</span>
                <div className="bid-bridge-label">− ARO</div>
                <div className="bid-bridge-value">−${aroMM.toFixed(2)}</div>
              </div>
              <div className="bid-bridge-arrow">→</div>
              <div className="bid-bridge-step">
                <span className="tag tag-model">Model</span>
                <div className="bid-bridge-label">Equity / asset PV</div>
                <div className="bid-bridge-value">${base.bidBridgePvMM.toFixed(2)}</div>
              </div>
              <div className="bid-bridge-arrow">→</div>
              <div className="bid-bridge-step highlight">
                <span className="tag tag-judgment">Bid</span>
                <div className="bid-bridge-label">Your bid</div>
                <div className="bid-bridge-value">${bidPriceMM.toFixed(2)}</div>
              </div>
              <div className="bid-bridge-arrow">→</div>
              <div className="bid-bridge-step highlight">
                <span className="tag tag-model">Computed</span>
                <div className="bid-bridge-label">IRR / MOIC</div>
                <div className="bid-bridge-value">
                  {fmtIrr(base.irrAnnual, base.undiscCfMM, bidPriceMM)} · {fmtMoic(base.moic)}
                </div>
              </div>
            </div>
            <p className="engine-note">
              Back-check multiples (not the bid frame): 4× ${base.multiples.x4.toFixed(1)}MM · 5× $
              {base.multiples.x5.toFixed(1)}MM · 6× ${base.multiples.x6.toFixed(1)}MM NTM CF
            </p>
          </section>

          {calibration && (
            <section className="dashboard-section">
              <div className="section-title">Model fidelity · cfScale</div>
              <div className="chart-card">
                <p className="chart-subtitle" style={{ marginBottom: 12 }}>
                  Decline streams are the seller forecast, then each asset CF is scaled so NPV10 @
                  base realized ≈ seller oneline. Large |cfScale − 1| means the decline/NRI path alone
                  does not match oneline — treat as fit residual, not physics.
                </p>
                <KPIGrid>
                  <KPICard
                    label="Model − seller PDP"
                    value={
                      calibration.modelVsRefPdpMM != null
                        ? `${calibration.modelVsRefPdpMM >= 0 ? '+' : ''}$${calibration.modelVsRefPdpMM.toFixed(2)}MM`
                        : '—'
                    }
                    sub={`Model $${calibration.modelBasePv10MM ?? '—'}MM · seller $${calibration.refPdpPv10MM ?? '—'}MM`}
                    color="var(--navy)"
                  />
                  <KPICard
                    label="cfScale median"
                    value={calibration.cfScale.median ?? '—'}
                    sub={`min ${calibration.cfScale.min} · max ${calibration.cfScale.max}`}
                    color="var(--amber)"
                  />
                  <KPICard
                    label="Boswell cfScale"
                    value={calibration.cfScale.boswell ?? '—'}
                    sub={
                      calibration.boswell
                        ? `NRI ${calibration.boswell.nri}% · DE ${(calibration.boswell.deAnnual * 100).toFixed(1)}%/yr`
                        : '—'
                    }
                    color="var(--green)"
                  />
                  <KPICard
                    label="Scale outliers"
                    value={String(calibration.cfScale.outlierCount)}
                    sub={`|cfScale − 1| > ${calibration.cfScale.outlierThreshold}`}
                    color="var(--red)"
                  />
                </KPIGrid>
                {calibration.topOutliers?.length > 0 && (
                  <DataTable
                    columns={[
                      { key: 'lease', label: 'Lease' },
                      { key: 'cfScale', label: 'cfScale', format: (v) => Number(v).toFixed(3) },
                      {
                        key: 'refPv10MM',
                        label: 'Oneline PV10',
                        format: (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}M`),
                      },
                      { key: 'nri', label: 'NRI %', format: (v) => (v == null ? '—' : Number(v).toFixed(1)) },
                    ]}
                    data={calibration.topOutliers}
                    defaultSort="cfScale"
                    onRowClick={drillWell}
                  />
                )}
              </div>
            </section>
          )}

          <section className="dashboard-section">
            <div className="section-title">Named scenarios at your bid</div>
            <div className="chart-subtitle" style={{ marginBottom: 8 }}>
              Click a scenario row for detail · deep link uses <code>scenario</code>
              {selectionContext?.scenarioId ? (
                <>
                  {' '}
                  · selected <code>{selectionContext.scenarioId}</code>
                </>
              ) : null}
            </div>
            <DataTable
              columns={[
                { key: 'scenario', label: 'Scenario' },
                { key: 'pv', label: base.pvLabel, format: (v) => `$${v.toFixed(2)}M` },
                { key: 'bridge', label: 'After ARO', format: (v) => `$${v.toFixed(2)}M` },
                { key: 'ntm', label: 'NTM CF', format: (v) => `$${v.toFixed(2)}M` },
                {
                  key: 'irr',
                  label: 'IRR @ bid',
                  format: (v) => fmtIrr(v),
                },
                {
                  key: 'x5',
                  label: '5× back-check',
                  format: (v) => `$${v.toFixed(2)}M`,
                },
              ]}
              data={scenarioRows}
              defaultSort="pv"
              onRowClick={drillScenario}
            />
          </section>

          <section className="dashboard-section" id="bid-submission">
            <div className="section-title">RedOaks bid submission</div>
            <div className="chart-card freeze-panel">
              <div className="chart-subtitle" style={{ marginBottom: 12 }}>
                Formal proposal for the advisor — written on letterhead, emailed to{' '}
                {process.submitTo?.name || 'David Carter'} by {process.bidDueLabel}. Separate from LP
                capital packs. Purchase price tracks the desk bid ($
                {bidPriceMM.toFixed(1)}MM).
              </div>
              <div className="freeze-grid">
                <label className="assumption-control">
                  <span>Letterhead entity</span>
                  <input
                    type="text"
                    value={proposal.letterheadEntity}
                    onChange={(e) => patchProposal({ letterheadEntity: e.target.value })}
                    placeholder="Firebrand Energy, LLC"
                  />
                </label>
                <label className="assumption-control">
                  <span>Effective date</span>
                  <input
                    type="date"
                    value={proposal.effectiveDate}
                    onChange={(e) => patchProposal({ effectiveDate: e.target.value })}
                  />
                </label>
                <label className="assumption-control">
                  <span>Valid through (min Aug 14)</span>
                  <input
                    type="date"
                    value={proposal.validThrough}
                    onChange={(e) => patchProposal({ validThrough: e.target.value })}
                  />
                </label>
              </div>
              <label className="assumption-control freeze-note" style={{ display: 'block', marginTop: 10 }}>
                <span>Financing narrative</span>
                <textarea
                  rows={3}
                  value={proposal.financingNarrative}
                  onChange={(e) => patchProposal({ financingNarrative: e.target.value })}
                  placeholder="Describe financing availability, sources, structure, and certainty through closing…"
                  style={{ width: '100%', marginTop: 4 }}
                />
              </label>
              <div className="freeze-grid" style={{ marginTop: 10 }}>
                <label className="assumption-control">
                  <span>Financing sources</span>
                  <input
                    type="text"
                    value={proposal.financingSources}
                    onChange={(e) => patchProposal({ financingSources: e.target.value })}
                    placeholder="e.g. Sponsor equity · committed LP capital"
                  />
                </label>
                <label className="assumption-control">
                  <span>Structure</span>
                  <input
                    type="text"
                    value={proposal.financingStructure}
                    onChange={(e) => patchProposal({ financingStructure: e.target.value })}
                    placeholder="e.g. All-cash at closing"
                  />
                </label>
                <label className="assumption-control">
                  <span>Certainty through close</span>
                  <input
                    type="text"
                    value={proposal.financingCertainty}
                    onChange={(e) => patchProposal({ financingCertainty: e.target.value })}
                    placeholder="e.g. Funds available; no financing out"
                  />
                </label>
              </div>
              <div style={{ marginTop: 12 }}>
                <div className="chart-title" style={{ fontSize: 13 }}>
                  Contingencies
                </div>
                {(proposal.contingencies || []).map((c, i) => (
                  <input
                    key={`cont-${i}`}
                    type="text"
                    className="assumption-control"
                    style={{ width: '100%', marginTop: 6 }}
                    value={c}
                    onChange={(e) => patchContingency(i, e.target.value)}
                  />
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    setProposal((prev) => ({
                      ...prev,
                      contingencies: [...(prev.contingencies || []), ''],
                    }))
                  }
                >
                  Add contingency
                </button>
              </div>
              <div style={{ marginTop: 14 }}>
                <div className="chart-title" style={{ fontSize: 13 }}>
                  Clarification contacts
                </div>
                {(proposal.contacts || []).map((c, i) => (
                  <div className="freeze-grid" key={`contact-${i}`} style={{ marginTop: 8 }}>
                    <label className="assumption-control">
                      <span>Name</span>
                      <input
                        type="text"
                        value={c.name}
                        onChange={(e) => patchContact(i, { name: e.target.value })}
                      />
                    </label>
                    <label className="assumption-control">
                      <span>Role</span>
                      <input
                        type="text"
                        value={c.role}
                        onChange={(e) => patchContact(i, { role: e.target.value })}
                      />
                    </label>
                    <label className="assumption-control">
                      <span>Email</span>
                      <input
                        type="email"
                        value={c.email}
                        onChange={(e) => patchContact(i, { email: e.target.value })}
                      />
                    </label>
                    <label className="assumption-control">
                      <span>Office</span>
                      <input
                        type="text"
                        value={c.office}
                        onChange={(e) => patchContact(i, { office: e.target.value })}
                      />
                    </label>
                    <label className="assumption-control">
                      <span>Mobile</span>
                      <input
                        type="text"
                        value={c.mobile}
                        onChange={(e) => patchContact(i, { mobile: e.target.value })}
                      />
                    </label>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    setProposal((prev) => ({
                      ...prev,
                      contacts: [
                        ...(prev.contacts || []),
                        { name: '', email: '', office: '', mobile: '', role: '' },
                      ],
                    }))
                  }
                >
                  Add contact
                </button>
              </div>
              <div className="release-readiness-panel" style={{ marginTop: 14 }}>
                <div className="chart-title">Submission checklist</div>
                <p className={`engine-note ${proposalGate.ok ? '' : 'error'}`}>
                  {proposalGate.ok
                    ? 'Proposal fields complete — export letter, put on letterhead PDF, email RedOaks.'
                    : `Incomplete: ${proposalGate.blockers.join('; ')}`}
                </p>
                <label className="assumption-check" style={{ display: 'block', marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!proposal.submission?.letterReviewed}
                    onChange={(e) => patchSubmission({ letterReviewed: e.target.checked })}
                  />
                  <span>Letter reviewed internally</span>
                </label>
                <label className="assumption-check" style={{ display: 'block', marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!proposal.submission?.letterheadPdfReady}
                    onChange={(e) => patchSubmission({ letterheadPdfReady: e.target.checked })}
                  />
                  <span>PDF on company letterhead ready</span>
                </label>
                <label className="assumption-check" style={{ display: 'block', marginTop: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!proposal.submission?.emailedToAdvisor}
                    onChange={(e) =>
                      patchSubmission({
                        emailedToAdvisor: e.target.checked,
                        submittedAt: e.target.checked
                          ? proposal.submission?.submittedAt || new Date().toISOString()
                          : null,
                      })
                    }
                  />
                  <span>
                    Emailed to {process.submitTo?.email || 'david.carter@redoaksadvisors.com'} (cc
                    Agnew / Garcia)
                  </span>
                </label>
              </div>
              <div className="freeze-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleExportBidLetter}
                  disabled={!freezePreview || !proposalGate.ok}
                >
                  Bid letter (.docx)
                </button>
                <button type="button" className="btn-secondary" onClick={handleCopyBidEmail}>
                  Copy email draft
                </button>
              </div>
              <p className="engine-note" style={{ marginTop: 10 }}>
                To: {process.submitTo?.email} · Cc:{' '}
                {(process.cc || []).map((c) => c.email).join('; ')} · Format:{' '}
                {process.formatNote}
              </p>
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-title">Named scenarios · capital pack</div>
            <div className="chart-card freeze-panel">
              <div className="chart-subtitle" style={{ marginBottom: 12 }}>
                Save knobs + returns under a name, reload anytime, then export branded Word / Excel for IC.
                Hash covers assumptions + results (not timestamps). Desk stays editable.
                Attach a saved scenario to a firm in{' '}
                <a href="#crm" className="linkish">
                  Investor CRM
                </a>{' '}
                to personalize outreach and open their portal.
              </div>
              <div className="freeze-grid">
                <label className="assumption-control">
                  <span>Scenario name</span>
                  <input
                    type="text"
                    value={freezeLabel}
                    onChange={(e) => {
                      setFreezeLabel(e.target.value);
                      setSaveMsg(null);
                    }}
                    placeholder="e.g. Pre-IC base · $12.5MM"
                  />
                </label>
                <label className="assumption-control">
                  <span>Approver</span>
                  <input
                    type="text"
                    value={freezeApprover}
                    onChange={(e) => setFreezeApprover(e.target.value)}
                    placeholder="Initials / name"
                  />
                </label>
                <label className="assumption-control freeze-note">
                  <span>Note (judgment)</span>
                  <input
                    type="text"
                    value={freezeNote}
                    onChange={(e) => setFreezeNote(e.target.value)}
                    placeholder="e.g. pre-IC · Boswell acid open"
                  />
                </label>
              </div>
              <div className="release-readiness-panel">
                <div className="chart-title">External-release evidence gate</div>
                <p className="chart-subtitle">
                  This remains a desk case until every material category has a logical source ID,
                  as-of date, owner, classification, disposition, and safe disclosure. Server
                  approval independently validates this contract.
                </p>
                <p className={`engine-note ${releaseGate.ready ? '' : 'error'}`}>
                  {releaseGate.ready
                    ? 'Release readiness complete — submit a server capital case for independent approval.'
                    : `External release blocked: ${releaseGate.blockers.length} item${releaseGate.blockers.length === 1 ? '' : 's'} need attention.`}
                </p>
                <div className="release-readiness-items">
                  {releaseReadiness.items.map((item) => (
                    <fieldset key={item.category} className="release-readiness-item">
                      <legend>{item.category.replace(/_/g, ' ')}</legend>
                      <input
                        aria-label={`${item.category} source reference`}
                        value={item.sourceRef}
                        onChange={(e) => updateReleaseItem(item.category, { sourceRef: e.target.value })}
                        placeholder="Logical source ID"
                      />
                      <input
                        aria-label={`${item.category} as of`}
                        value={item.asOf}
                        onChange={(e) => updateReleaseItem(item.category, { asOf: e.target.value })}
                        placeholder="As-of date"
                      />
                      <input
                        aria-label={`${item.category} owner`}
                        value={item.owner}
                        onChange={(e) => updateReleaseItem(item.category, { owner: e.target.value })}
                        placeholder="Owner"
                      />
                      <select
                        aria-label={`${item.category} classification`}
                        value={item.classification}
                        onChange={(e) => updateReleaseItem(item.category, { classification: e.target.value })}
                      >
                        <option value="fact">Fact</option>
                        <option value="model">Model</option>
                        <option value="judgment">Judgment</option>
                      </select>
                      <select
                        aria-label={`${item.category} disposition`}
                        value={item.disposition}
                        onChange={(e) => updateReleaseItem(item.category, { disposition: e.target.value })}
                      >
                        <option value="block">Block</option>
                        <option value="disclosed">Disclose</option>
                        <option value="cleared">Cleared</option>
                      </select>
                      {item.treatment !== undefined && (
                        <input
                          aria-label={`${item.category} treatment`}
                          value={item.treatment}
                          onChange={(e) => updateReleaseItem(item.category, { treatment: e.target.value })}
                          placeholder="Explicit treatment, including zero"
                        />
                      )}
                      <textarea
                        aria-label={`${item.category} disclosure`}
                        rows={2}
                        value={item.summary}
                        onChange={(e) => updateReleaseItem(item.category, { summary: e.target.value })}
                        placeholder="External-safe summary / disclosure"
                      />
                    </fieldset>
                  ))}
                </div>
              </div>
              {freezePreview && (
                <p className="engine-note" style={{ marginTop: 12 }}>
                  Live hash <code>{freezePreview.contentHash}</code> · bid $
                  {freezePreview.assumptions.bidPriceMM.toFixed(1)}MM · base IRR{' '}
                  {fmtIrr(freezePreview.results.base?.irrAnnual)} · stress IRR{' '}
                  {fmtIrr(freezePreview.results.stress?.irrAnnual)}
                  {activeScenario ? (
                    <>
                      {' '}
                      · active save <code>{activeScenario.label}</code>
                    </>
                  ) : null}
                </p>
              )}
              <div className="freeze-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveScenario}
                  disabled={!freezePreview || !freezeLabel.trim()}
                >
                  Save scenario
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleExportIcMemo}
                  disabled={!exportSource}
                >
                  IC memo (.docx)
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleExportOnePager}
                  disabled={!exportSource || !evaluateReleaseReadiness(exportSource.releaseReadiness).ready}
                >
                  One-pager (.docx)
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleExportUnderwriting}
                  disabled={!exportSource}
                >
                  Underwriting (.xlsx)
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleExportRegister}
                  disabled={!register.length && !freezePreview}
                >
                  Register (.xlsx)
                </button>
              </div>
              {saveMsg && (
                <p className="engine-note" style={{ marginTop: 10 }}>
                  {saveMsg}
                </p>
              )}
              {register.length > 0 && (
                <div className="saved-scenario-list" style={{ marginTop: 16 }}>
                  <div className="chart-title">Saved scenarios ({register.length})</div>
                  <ul className="saved-scenario-ul">
                    {register.map((f) => (
                      <li
                        key={f.scenarioId}
                        className={
                          activeScenario?.scenarioId === f.scenarioId
                            ? 'saved-scenario-row active'
                            : 'saved-scenario-row'
                        }
                      >
                        <div className="saved-scenario-meta">
                          <strong>{f.label}</strong>
                          <span>
                            {String(f.frozenAt).slice(0, 16).replace('T', ' ')} · bid $
                            {Number(f.assumptions.bidPriceMM).toFixed(1)}MM · IRR{' '}
                            {fmtIrr(f.results.base?.irrAnnual)} ·{' '}
                            <code>{f.contentHash}</code>
                          </span>
                        </div>
                        <div className="saved-scenario-actions">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => handleLoadScenario(f.scenarioId)}
                          >
                            Load
                          </button>
                          <button
                            type="button"
                            className="btn-secondary danger"
                            onClick={() => handleDeleteScenario(f.scenarioId)}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {register.length > 1 && (
                <div className="freeze-compare" style={{ marginTop: 16 }}>
                  <div className="chart-title">Compare saved scenarios</div>
                  <div className="freeze-grid">
                    <label className="assumption-control">
                      <span>Left</span>
                      <select value={compareLeftId} onChange={(e) => setCompareLeftId(e.target.value)}>
                        <option value="">Select…</option>
                        {register.map((f) => (
                          <option key={f.scenarioId} value={f.scenarioId}>
                            {f.label} · {f.contentHash}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="assumption-control">
                      <span>Right</span>
                      <select value={compareRightId} onChange={(e) => setCompareRightId(e.target.value)}>
                        <option value="">Select…</option>
                        {register.map((f) => (
                          <option key={f.scenarioId} value={f.scenarioId}>
                            {f.label} · {f.contentHash}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {compareResult && (
                    <DataTable
                      columns={[
                        { key: 'metric', label: 'Metric' },
                        { key: 'left', label: compareResult.left.label || 'Left' },
                        { key: 'right', label: compareResult.right.label || 'Right' },
                        { key: 'delta', label: 'Δ (right − left)' },
                      ]}
                      data={[
                        {
                          metric: 'PV ($MM)',
                          left: compareResult.pvMM.left != null ? `$${compareResult.pvMM.left.toFixed(2)}` : '—',
                          right: compareResult.pvMM.right != null ? `$${compareResult.pvMM.right.toFixed(2)}` : '—',
                          delta: compareResult.pvMM.delta != null ? `${compareResult.pvMM.delta >= 0 ? '+' : ''}$${compareResult.pvMM.delta.toFixed(2)}` : '—',
                        },
                        {
                          metric: 'IRR @ bid',
                          left: fmtIrr(compareResult.irrAnnual.left),
                          right: fmtIrr(compareResult.irrAnnual.right),
                          delta: compareResult.irrAnnual.delta != null ? `${compareResult.irrAnnual.delta >= 0 ? '+' : ''}${(compareResult.irrAnnual.delta * 100).toFixed(1)}pp` : '—',
                        },
                        {
                          metric: 'Buyer OH included ($/mo)',
                          left: formatCurrency(compareResult.buyerOhMonthlyIncluded.left),
                          right: formatCurrency(compareResult.buyerOhMonthlyIncluded.right),
                          delta: compareResult.buyerOhMonthlyIncluded.delta != null ? formatCurrency(compareResult.buyerOhMonthlyIncluded.delta) : '—',
                        },
                        {
                          metric: 'Liability oneline total',
                          left: formatCurrency(compareResult.liabilityOnelineTotal.left),
                          right: formatCurrency(compareResult.liabilityOnelineTotal.right),
                          delta: compareResult.liabilityOnelineTotal.delta != null ? formatCurrency(compareResult.liabilityOnelineTotal.delta) : '—',
                        },
                        {
                          metric: 'After ARO ($MM)',
                          left: compareResult.bidBridgePvMM.left != null ? `$${compareResult.bidBridgePvMM.left.toFixed(2)}` : '—',
                          right: compareResult.bidBridgePvMM.right != null ? `$${compareResult.bidBridgePvMM.right.toFixed(2)}` : '—',
                          delta: compareResult.bidBridgePvMM.delta != null ? `${compareResult.bidBridgePvMM.delta >= 0 ? '+' : ''}$${compareResult.bidBridgePvMM.delta.toFixed(2)}` : '—',
                        },
                      ]}
                      defaultSort="metric"
                      defaultDir="asc"
                    />
                  )}
                  {!compareResult && (
                    <p className="engine-note">Pick two saved scenarios to compare PV / IRR / OH / liabilities.</p>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
